/**
 * Comparable forms of food names, one rule for every app (docs/normalisierung.md). Case, umlauts,
 * accents and the common German plural endings do not matter: "Tomaten" and "tomate", "Äpfel" and
 * "Apfel" meet.
 *
 * Decided on 18.10.2026 (H1, docs/normalisierung.md): "ä" becomes "a", the rule the EinkaufsZettel
 * stores its keys in. Ids are a different matter: they spell umlauts out ("gewuerze") and never
 * change once given (productId below).
 */

/** Lower case, ä → a, ß → ss, accents dropped, punctuation to spaces, single spaces. */
export function fold(text: string): string {
  return text
    .toLowerCase()
    .replaceAll("ß", "ss")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/** "ae", "oe", "ue" as typed for an umlaut, read as the folded umlaut ("muesli" → "musli"). */
export function foldSpelledOut(text: string): string {
  return fold(text).replace(/([aou])e/g, "$1");
}

const IRREGULAR: Record<string, string> = { eier: "ei" };

/**
 * A rough stem of one folded word. Only what shopping lists need: the same stem for singular and
 * plural of one product. Different products may share a stem now and then; that only costs a
 * wrong duplicate hint, never data.
 */
export function stem(word: string): string {
  const irregular = IRREGULAR[word];
  if (irregular) return irregular;
  if (word.length > 4 && word.endsWith("en")) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("e")) return word.slice(0, -1);
  if (word.length > 4 && (word.endsWith("ln") || word.endsWith("rn"))) return word.slice(0, -1);
  if (word.length > 4 && word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return word;
}

/** The key two names share when they name the same product: folded, every word stemmed. */
export function nameKey(name: string): string {
  return fold(name)
    .split(" ")
    .filter(Boolean)
    .map(stem)
    .join(" ");
}

/** Folded, stemmed and written-together forms of a name: all of them find its product. */
export function keysOf(name: string): string[] {
  const folded = fold(name);
  const stemmed = folded.split(" ").map(stem).join(" ");
  return [...new Set([folded, stemmed, folded.replaceAll(" ", ""), stemmed.replaceAll(" ", "")])].filter(Boolean);
}

/**
 * The id a new product gets from its first name: umlauts spelled out, accents dropped, words
 * joined by "-" ("Rote Zwiebel" → "rote-zwiebel", "Süßkartoffel" → "suesskartoffel"). The same rule
 * as the aisle ids of the EinkaufsZettel ("gewuerze") and Lagerlog's copy of the catalogue. Only
 * for giving an id; an id once given stays, whatever happens to the name.
 */
export function productId(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/ /g, "-");
}
